import { createSelector } from 'reselect';
import moment from 'moment';
import groupBy from 'lodash.groupby';

const statePolicySelector = (state) => state.policy.items;

const updatePolicyDueDates = (policy) => {
	const { isPartOfAccountBill, isPrimaryAccountPolicy } = policy.billing;
	const isAccountBill = isPartOfAccountBill && isPrimaryAccountPolicy;

	const isPastDue = Math.max(isAccountBill ? policy.billing.accountOutstandingBalance : policy.billing.outstandingBalance, 0) >= 1 && moment(policy.billing.outstandingBalanceDueDate).isBefore(moment());
	policy.isPastDue = isPastDue;
	policy.isSoonDue = Math.max(isAccountBill ? policy.billing.accountOutstandingBalance : policy.billing.outstandingBalance, 0) >= 1 && !isPastDue && moment().isAfter(moment(policy.billing.outstandingBalanceDueDate).subtract(7, 'days'));
};

export const additionalInfoPoliciesSelector = createSelector([statePolicySelector], (items) => {
	return items.map((policy) => {
		updatePolicyDueDates(policy);

		if (policy.futurePolicy) {
			updatePolicyDueDates(policy.futurePolicy);
		}

		return policy;
	});
});

export const sortedPoliciesSelector = createSelector([additionalInfoPoliciesSelector], (items) => {
	const _items = [...(items || [])];

	var balanceItems = _items.filter((i) => i.billing.outstandingBalance >= 1);
	balanceItems.sort((a, b) => {
		const aOB = moment(a.billing.outstandingBalanceDueDate);
		const bOB = moment(b.billing.outstandingBalanceDueDate);

		return aOB.diff(bOB, 'day') || a.policyNumber.localeCompare(b.policyNumber);
	});

	var otherItems = _items.filter((i) => i.billing.outstandingBalance < 1);
	otherItems.sort((a, b) => {
		return a.policyNumber.localeCompare(b.policyNumber);
	});

	return [...balanceItems, ...otherItems];
});

export const paymentListSelector = createSelector([sortedPoliciesSelector], (items) => {
	const allPolicies = [];

	items.forEach((item) => {
		item.description = 
		[
			"Current Term Effective",
			`${moment(item.term.effectiveDate).format('L')} - ${moment(item.term.endDate).format('L')}`
		];
		item.header = item.policyNumber;
		allPolicies.push(item);

		if (item.futurePolicy) {
			const future = item.futurePolicy;
			future.isFuture = true;
			future.nickname = item.nickname;
			future.description = [
                "Renewal Effective",
                `${moment(future.term.effectiveDate).format('L')} - ${moment(future.term.endDate).format('L')}`
            ];
			allPolicies.push(future);
		}
	});

	const policies = allPolicies
		.filter((p) => !p.billing.isPartOfAccountBill && !p.isFuture)
		.map((p) => {
			const item = {
				header: p.policyNumber,
				current: p,
				future: p.futurePolicy,
			};

			delete p.futurePolicy;
			return item;
		});

	const accountPolicies = allPolicies.filter((p) => p.billing.isPartOfAccountBill);
	const allAccountGroups = groupBy(accountPolicies, (p) => `${p.billing.accountNumber}${p.isFuture ? '(renewal)' : ''}`);
	const accountGroups = [];

	const createAccountGroup = (aps) => {
		if (!aps || aps.length === 0) {
			return null;
		}

		const primary = aps.find((p) => p.billing.isPrimaryAccountPolicy);
		if (!primary.isFuture || primary.billing.accountOutstandingBalance > 0) {
			return {
				description: primary.description,
				subItems: [primary, ...aps.filter((ap) => ap !== primary)],
				total: {
					isPastDue: primary.isPastDue,
					isSoonDue: primary.isSoonDue,
					billing: {
						outstandingBalance: primary.billing.accountOutstandingBalance,
						outstandingBalanceDueDate: primary.billing.outstandingBalanceDueDate,
						payInFullAmount: primary.billing.accountPayInFull,
						lastPayment: primary.billing.lastPayment
							? {
									paymentAmount: primary.billing.lastPayment.accountPaymentAmount,
									itemDate: primary.billing.itemDate,
							  }
							: null,
					},
				},
			};
		}

		return null;
	};

	Object.keys(allAccountGroups)
		.filter((agKey) => !agKey.includes('(renewal)'))
		.forEach((agKey) => {
			const aps = allAccountGroups[agKey];
			const primary = aps.find((p) => p.billing.isPrimaryAccountPolicy);
			if (!primary.isFuture || primary.billing.accountOutstandingBalance > 0) {
				accountGroups.push({
					header: primary.billing.accountNumber,
					current: createAccountGroup(aps),
					future: createAccountGroup(allAccountGroups[agKey + '(renewal)']),
				});
			}
		});

	return { policies, accountGroups };
});